/**
 * Özet (v0.1.175; the summary dashboard v0.1.257): GET /ozet (what waits for Sercan, the team) and GET /ozet/summary
 * for the chosen period (Bugün, Bu hafta, Bu ay, Son 30 gün). Both are read when the page opens, again on Yenile and
 * every minute while the page is open and the tab is visible (the prospects watch() pattern); a period switch reads
 * only the summary. The last view of each period stays on screen while it loads. Read only.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import { OZET_DEFAULT_PERIOD, type OzetPeriod, type OzetSummaryView, type OzetView } from '../api/ozet.js';
import { useStore } from './context.js';

export const OZET_REFRESH_MS = 60_000;

export interface OzetState {
  view: OzetView | null;
  error: string | null;
  loading: boolean;
  period: OzetPeriod;
  /** The last summary read of each period. */
  summaries: Readonly<Partial<Record<OzetPeriod, OzetSummaryView>>>;
  summaryError: string | null;
  summaryLoading: boolean;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export class OzetStore {
  private state: OzetState = {
    view: null,
    error: null,
    loading: false,
    period: OZET_DEFAULT_PERIOD,
    summaries: {},
    summaryError: null,
    summaryLoading: false,
  };
  private inFlight: Promise<void> | null = null;
  private readonly summaryInFlight = new Map<OzetPeriod, Promise<void>>();
  private readonly listeners = new Set<() => void>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private watchers = 0;

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): OzetState => this.state;

  private set(patch: Partial<OzetState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** Reads GET /ozet again, keeping the current view meanwhile; one read at a time. */
  reload(): Promise<void> {
    if (this.inFlight !== null) return this.inFlight;
    this.set({ loading: true });
    const run = (async () => {
      try {
        this.set({ view: await this.api.getOzet(), error: null, loading: false });
      } catch (error) {
        this.set({ error: errorText(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }

  /** Reads the summary of the chosen period; one read per period at a time. */
  reloadSummary(): Promise<void> {
    const period = this.state.period;
    const running = this.summaryInFlight.get(period);
    if (running !== undefined) return running;
    this.set({ summaryLoading: true });
    const run = (async () => {
      try {
        const summary = await this.api.getOzetSummary(period);
        const patch: Partial<OzetState> = { summaries: { ...this.state.summaries, [period]: summary } };
        // An answer for a period Sercan has left updates the cache, not the page's error and loading line.
        if (this.state.period === period) Object.assign(patch, { summaryError: null, summaryLoading: false });
        this.set(patch);
      } catch (error) {
        if (this.state.period === period) this.set({ summaryError: errorText(error), summaryLoading: false });
      }
    })();
    this.summaryInFlight.set(period, run);
    void run.finally(() => {
      if (this.summaryInFlight.get(period) === run) this.summaryInFlight.delete(period);
    });
    return run;
  }

  /** Bugün, Bu hafta, Bu ay or Son 30 gün: the page shows that period's last read at once and reads it again. */
  setPeriod(period: OzetPeriod): Promise<void> {
    if (period === this.state.period) return this.reloadSummary();
    this.set({ period, summaryError: null, summaryLoading: false });
    return this.reloadSummary();
  }

  /** Both reads (Yenile, the minute timer). */
  refresh(): Promise<void> {
    return Promise.all([this.reload(), this.reloadSummary()]).then(() => undefined);
  }

  /** The page calls this on mount; the returned function stops the minute refresh on unmount. */
  watch(): () => void {
    this.watchers += 1;
    void this.refresh();
    if (this.timer === null && typeof setInterval === 'function') {
      this.timer = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        void this.refresh();
      }, OZET_REFRESH_MS);
    }
    return () => {
      this.watchers = Math.max(0, this.watchers - 1);
      if (this.watchers === 0 && this.timer !== null) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  }
}

export function useOzet(): { store: OzetStore; state: OzetState } {
  const store = useStore().ozet;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
